import { PostHogProvider as PHProvider, usePostHog } from "posthog-js/react"
import posthog from "posthog-js"
import { type ReactNode, useEffect } from "react"
import { useLocation } from "react-router"

// PostHog's project API key + host are PUBLIC client-side values (one project across all
// envs), so we hardcode safe defaults instead of depending on per-deploy build env vars.
// Env vars still win when set; the defaults just guarantee PostHog (analytics + feature
// flags) loads even when a deploy target's Docker build doesn't forward VITE_PUBLIC_POSTHOG_*.
const key = import.meta.env.VITE_PUBLIC_POSTHOG_KEY || "phc_v1chk8oXyctkeeH0VARWfuvZ09yw47WT5ysXhKqA3qQ"
const host = import.meta.env.VITE_PUBLIC_POSTHOG_HOST || "https://us.i.posthog.com"

// Resolves once feature flags have been loaded by PostHog. Used by route loaders to gate
// flag-aware routing decisions on a stable signal instead of racing init.
//
// Hard timeout: if PostHog is blocked (ad blocker, network issue, blocked CSP), the loaded
// callback never fires and routes would hang forever. Cap the wait at 1.5s — flag defaults
// to OFF if we time out, which is the safe default (no behavioral change vs flag-on).
const FLAG_BOOTSTRAP_TIMEOUT_MS = 1500
let flagsLoadedResolve: () => void = () => {}
export const flagsLoaded = new Promise<void>((resolve) => {
  flagsLoadedResolve = resolve
})

if (typeof window !== "undefined") {
  // Always set the safety-net timer first so loaders can never block forever.
  setTimeout(() => flagsLoadedResolve(), FLAG_BOOTSTRAP_TIMEOUT_MS)

  if (key && host && import.meta.env.MODE !== "test") {
    posthog.init(key, {
      api_host: host,
      capture_pageview: false,
      loaded: (ph) => {
        ph.register({
          environment: import.meta.env.VITE_ENV || import.meta.env.MODE || "development",
        })
        ph.onFeatureFlags(() => {
          flagsLoadedResolve()
        })
      },
    })
  } else {
    // No PostHog configured (dev without env vars). Resolve immediately so loaders don't wait.
    flagsLoadedResolve()
  }
}

/**
 * Sync flag check. Use in route loaders after `await flagsLoaded`.
 *
 * Returns false when PostHog isn't initialized (no env vars) — flag-OFF is the safe default.
 */
export function isFlagEnabled(key: string): boolean {
  if (typeof window === "undefined") return false
  try {
    return posthog.isFeatureEnabled?.(key) === true
  } catch {
    return false
  }
}

/**
 * Identify the current user with PostHog. Called from SessionContextProvider when a session resolves.
 * Includes organization_id for staff-targeting feature flags.
 *
 * Property semantics matter here, because posthog.identify MERGES:
 *   - omit a key entirely -> leave whatever is already set
 *   - pass null           -> clear it
 *   - pass undefined      -> dropped on serialisation, i.e. same as omitting
 *
 * So `organization_id` is only written when the caller actually supplies the key,
 * and an explicit null clears it rather than being silently swallowed. Without that
 * distinction the two-phase identify (email first, org after resolution) would either
 * clobber the org on every first call, or — for internal staff leaving an acting
 * session — leave the customer's organization_id attached to their identity, so
 * org-targeted flags would keep evaluating as that customer.
 */
export function identifyUser(
  userId: string,
  props: { email?: string | null; organization_id?: string | null } = {},
): void {
  if (typeof window === "undefined" || !key) return
  try {
    const properties: Record<string, unknown> = {}

    if (props.email !== undefined) {
      properties.email = props.email ?? undefined
    }

    if ("organization_id" in props) {
      properties.organization_id = props.organization_id ?? null
    }

    posthog.identify(userId, properties)
  } catch (error) {
    console.warn("posthog.identify failed:", error)
  }
}

/** Reset PostHog identity on sign-out. */
export function resetUser(): void {
  if (typeof window === "undefined" || !key) return
  try {
    posthog.reset()
  } catch (error) {
    console.warn("posthog.reset failed:", error)
  }
}

const PostHogPageviewTracker = () => {
  const location = useLocation()
  const ph = usePostHog()

  useEffect(() => {
    ph?.capture("$pageview", {
      $current_url: window.location.href,
    })
  }, [location.pathname, location.search, ph])

  return null
}

export const PostHogProvider = ({ children }: { children: ReactNode }) => {
  if (!key || !host) {
    return <>{children}</>
  }

  return (
    <PHProvider client={posthog}>
      <PostHogPageviewTracker />
      {children}
    </PHProvider>
  )
}
